import { html } from "lit-html";
import i18next from "i18next";
import classNames from "classnames";
import { useRef } from "haunted";
import PerfectScrollbar from "perfect-scrollbar";
import { ALL_COUNTRY_CODE } from "./useStationSelector";
import { stationSelectorCountryArrowDesktop, stationSelectorCountryArrowMobile } from "./newFlightSvgs";
import { stationUtils } from "./stationUtils";
import { useEffectWithoutCleanup, useMemo, useState } from "Shared/haunted/CustomHooks";
import { ref } from "Components/directives/ref";
import { City, Country } from "./RoutePickerModel";
import { useWindowSize } from "../../../../shared/haunted/useWindowSize";
import { INBOUND, OUTBOUND } from "Services/constants";
import { DynamicSettings } from "Components/admin/DynamicSettings";

export interface Props {
	countries: Country[];
	culture: string;
	dynamicSettings: DynamicSettings;
	destinationCity: City;
	isOpen: boolean;
	journeyIndex: number;
	originCity: City;
	onInput: () => void;
	onClick: (city: City) => void;
}

export const useStationSelectorDropdown = (props: Props) => {
	const allStationsPlaceholder: Country = {
		cities: [],
		code: ALL_COUNTRY_CODE,
		isAvailableForPeruCompras: false,
		name: i18next.t("Todos los destinos"),
	};

	const windowSize = useWindowSize();

	const { getFilteredModel } = stationUtils();

	const scroller = useRef<HTMLDivElement>(null);

	const [selectedCountryCode, setSelectedCountryCode] = useState<string>("");
	const [filterExpression, setFilterExpression] = useState<string>("");

	const filteredModel = useMemo(
		() => getFilteredModel(props.countries, filterExpression) || [],
		[props.countries, filterExpression]
	);

	const orderedCountries = useMemo(() => {
		if (!filteredModel?.length) return [];

		const selectedCountry = filteredModel.find((country) => country.code === selectedCountryCode);

		return selectedCountry && selectedCountryCode !== ALL_COUNTRY_CODE
			? [
					selectedCountry,
					allStationsPlaceholder,
					...filteredModel.filter((country) => country.code !== selectedCountryCode),
			  ]
			: [allStationsPlaceholder, ...filteredModel];
	}, [selectedCountryCode, filteredModel]);

	const init = () => {
		if (!props.dynamicSettings || !orderedCountries.length || selectedCountryCode) return;

		const defaultCountries = props.dynamicSettings.DefaultRouteCountrySettings?.find(
			(setting) => setting.Culture.toLowerCase() === props.culture.toLowerCase()
		);

		const defaultCountry =
			(props.journeyIndex === OUTBOUND
				? defaultCountries?.DefaultOriginCountryCode
				: defaultCountries?.DefaultDestinationCountryCode) || props.culture.substring(3, 5);

		const cultureBasedCountry = orderedCountries.find(
			(country) => country.code.toLowerCase() === defaultCountry.toLowerCase()
		);

		setSelectedCountryCode(cultureBasedCountry?.code || ALL_COUNTRY_CODE);
	};

	const initScrollbar = () => {
		let scrollbar: PerfectScrollbar;

		window.setTimeout(() => {
			if (scroller.current?.parentElement && windowSize.width > 1023) {
				scrollbar = new PerfectScrollbar(scroller.current.parentElement, {
					wheelPropagation: false,
					wheelSpeed: 2,
					swipeEasing: true,
					suppressScrollX: true,
				});
			}
		}, 100);

		return () => scrollbar?.destroy();
	};

	// DEVNOTE We put the longest list with the shortest list and so on to make as many fit as possible
	const groupedCountries = useMemo(() => {
		if (!orderedCountries?.length) return [];

		const orderedRealCountries = orderedCountries.filter((country) => country.code !== ALL_COUNTRY_CODE);

		if (windowSize.width < 1024) return [orderedRealCountries];

		const sorted = orderedRealCountries.sort((a, b) => a.cities.length - b.cities.length);
		const result: Country[][] = [];

		while (sorted.length > 0) {
			const shortest = sorted.shift()!;
			const longest = sorted.pop() || null;
			result.push([longest, shortest]);
		}

		return result;
	}, [orderedCountries]);

	const isReady = props.countries && selectedCountryCode && (filterExpression || groupedCountries?.length);

	const handleCountryClick = (e: MouseEvent, country: Country) => {
		e.preventDefault();
		e.stopPropagation();
		setSelectedCountryCode(country.code);
	};

	const handleInput = (e: KeyboardEvent) => {
		props.onInput();
		setFilterExpression((e.target as HTMLInputElement).value);
	};

	const handleCityClick = (city: City) => {
		setFilterExpression("");
		setSelectedCountryCode(city.countryCode);
		props.onClick(city);
	};

	useEffectWithoutCleanup(() => {
		if (!filteredModel?.some((c) => c.code === selectedCountryCode) && selectedCountryCode !== ALL_COUNTRY_CODE) {
			const firstCountry = filteredModel?.[0]?.code;
			if (firstCountry) setSelectedCountryCode(firstCountry);
		}
	}, [props.isOpen, selectedCountryCode, filteredModel]);

	useEffectWithoutCleanup(() => {
		if (filterExpression) setSelectedCountryCode(ALL_COUNTRY_CODE);
	}, [filterExpression]);

	useEffectWithoutCleanup(initScrollbar, [props.countries, selectedCountryCode]);

	useEffectWithoutCleanup(init, [props.isOpen, orderedCountries, props.dynamicSettings, selectedCountryCode]);

	useEffectWithoutCleanup(() => {
		const newCountryCode =
			props.journeyIndex === OUTBOUND ? props.originCity?.countryCode : props.destinationCity?.countryCode;
		if (newCountryCode && newCountryCode !== selectedCountryCode) setSelectedCountryCode(newCountryCode);
	}, [props.originCity?.code, props.destinationCity?.code]);

	const countryNameTemplate = (countryName: string, index: number) => html`
		<span
			class=${classNames(
				"whitespace-nowrap font-body text-lg font-bold leading-5 text-n-blue",
				"md:font-normal",
				{
					"md:group-hover:text-n-cyan": index !== 0,
					"md:text-[#636363]": index !== 0,
					"md:text-white": index === 0,
				}
			)}
		>
			${countryName}
		</span>
	`;

	const countryArrowTemplateDesktop = (index: number) => html`
		<span
			class=${classNames("mt-1 tw-hidden", "md:block", {
				"md:text-[#636363]": index !== 0,
				"md:text-white": index === 0,
				"md:group-hover:text-n-cyan": index !== 0,
			})}
		>
			${stationSelectorCountryArrowDesktop}
		</span>
	`;

	const countryArrowTemplateMobile = (countryCode: string) => html`
		<span
			class=${classNames("common-transition", "md:hidden", {
				"mt-1.5 rotate-180 transform": selectedCountryCode !== countryCode,
			})}
		>
			${stationSelectorCountryArrowMobile}
		</span>
	`;

	const countryTemplate = (country: Country, index: number) => html`
		<li
			class=${classNames(
				"group flex h-8 cursor-pointer items-center justify-between px-4",
				"md:h-10 md:gap-x-4 md:px-5",
				{
					"md:bg-n-cyan": index === 0,
				}
			)}
			@click=${(e: MouseEvent) => handleCountryClick(e, country)}
		>
			${countryNameTemplate(country.name, index)} ${countryArrowTemplateDesktop(index)}
			${countryArrowTemplateMobile(country.code)}
		</li>
	`;

	const cityTemplate = (city: City) => html`
		<li
			class=${classNames(
				"group flex w-full cursor-pointer justify-between gap-x-4 font-body text-sm leading-6",
				"md:leading-[18px]"
			)}
			@click=${() => handleCityClick(city)}
		>
			<span class=${classNames("text-[#444444]", "md:group-hover:text-n-cyan")}> ${city.name} </span>
			<span class=${classNames("text-[#a1a1a1]", "md:group-hover:text-n-cyan")}> ${city.code} </span>
		</li>
	`;

	const citiesTemplate = () => html`
		<li class=${classNames("mb-2 font-body text-base font-bold leading-5 text-n-cyan tw-hidden", "md:block")}>
			${i18next.t("Ciudades")}
		</li>
		${filteredModel.find((country) => country.code === selectedCountryCode)?.cities.map(cityTemplate)}
	`;

	const allCountriesCountryTemplate = (country: Country, index: number) =>
		country
			? html`
					<div
						class=${classNames("mb-2 border-b border-solid border-[#e0e0e0] pb-2", {
							"md:mb-0 md:border-none md:pb-0": index !== 0,
						})}
					>
						<div class="mb-2 font-body text-base font-bold leading-5 text-n-cyan">${country.name}</div>
						<ul>
							${country.cities.map(cityTemplate)}
						</ul>
					</div>
			  `
			: "";

	const countryGroupTemplate = (countryGroup: Country[]) => html`
		<div>${countryGroup.map(allCountriesCountryTemplate)}</div>
	`;

	const allDestinationsTemplate = () => html`
		<li
			ref=${ref(scroller)}
			class=${classNames("relative", "md:grid md:h-[416px] md:grid-cols-[auto_auto_auto_auto] md:gap-x-10")}
		>
			${groupedCountries.map(countryGroupTemplate)}
		</li>
	`;

	const noResultTemplate = () =>
		html`<div class="bg-white p-5 font-body text-lg leading-5 text-n-red">${i18next.t("No hay resultados.")}</div>`;

	const mobileTemplate = () => html`
		<div class=${classNames("flex max-h-[267px] flex-col overflow-y-scroll rounded-lg bg-white", "md:hidden")}>
			<ul>
				${countryTemplate(orderedCountries[0], 0)}
			</ul>
			<ul class=${classNames("shrink-0 overflow-hidden px-6")}>
				${selectedCountryCode === ALL_COUNTRY_CODE ? allDestinationsTemplate() : citiesTemplate()}
			</ul>
			<ul>
				${orderedCountries.slice(1).map(countryTemplate)}
			</ul>
		</div>
	`;

	const desktopTemplate = () => html`
		<div class=${classNames("max-h-full items-stretch overflow-hidden rounded-lg bg-white tw-hidden", "md:flex")}>
			<ul class=${classNames("flex-1 tw-hidden", "md:block")}>
				${orderedCountries.map(countryTemplate)}
			</ul>
			<ul class=${classNames("max-h-[416px] min-w-52 shrink-0 overflow-hidden px-5 py-5")}>
				${selectedCountryCode === ALL_COUNTRY_CODE ? allDestinationsTemplate() : citiesTemplate()}
			</ul>
		</div>
	`;

	const contentTemplate = () =>
		orderedCountries?.length ? html`${desktopTemplate()} ${mobileTemplate()}` : noResultTemplate();

	const inputValue = (props.journeyIndex === OUTBOUND ? props.originCity?.name : props.destinationCity?.name) || "";

	const inputTemplate = () => html`
		<input
			class=${classNames(
				"w-full cursor-pointer appearance-none border-none bg-transparent px-0 pb-0 pt-0.5 text-base outline-none",
				"placeholder:text-base placeholder:text-[#c1c1c1]",
				"focus:cursor-text",
				{
					"text-n-blue": inputValue,
				}
			)}
			placeholder=${props.journeyIndex === OUTBOUND ? i18next.t("Origen") : i18next.t("Destino")}
			value=${filterExpression || inputValue || ""}
			.value=${filterExpression || inputValue || ""}
			@input=${handleInput}
		/>
	`;

	const htmlTemplate = () =>
		isReady || props.journeyIndex === INBOUND
			? html`
					${inputTemplate()}
					<ac-dropdown2
						.content=${contentTemplate()}
						.isOpen=${props.isOpen}
						.useParentWidth=${windowSize.width < 1024}
					></ac-dropdown2>
			  `
			: "";

	return { htmlTemplate };
};
